import type { Translator } from '@apm/i18n';
import { formatClock } from '@apm/i18n';

import type { MessageKey } from '../../i18n/ko.js';
import type { ItemBrief } from '../../home/compose-home.js';
import type { WatchNowLabel } from '../../home/watch-target.js';

/**
 * 홈의 문구 — **편성표의 키를 다시 쓴다** (`docs/63` §7). 회차는 `episodes.label*`, 이어보기는
 * `episodes.resumeAt`, 시각은 `@apm/i18n` 의 `formatClock`. 새로 만들면 편성표와 다른 말이 된다.
 */
export function episodeText(t: Translator<MessageKey>, item: ItemBrief): string | null {
  if (item.episodeNo === null) return null;
  return item.seasonNo === null
    ? t('episodes.labelNoSeason', { episode: item.episodeNo })
    : t('episodes.label', { season: item.seasonNo, episode: item.episodeNo });
}

/** 「지금」 줄 — 회차가 있으면 「S1 · 3회 「제목」」, 없으면 제목만 */
export function itemText(t: Translator<MessageKey>, item: ItemBrief): string {
  const episode = episodeText(t, item);
  return episode === null ? item.title : `${episode} 「${item.title}」`;
}

/** masthead CTA 의 문구 — 행의 종류가 정한다 (`docs/63` §4.4) */
export function ctaText(
  t: Translator<MessageKey>,
  label: WatchNowLabel,
  now: ItemBrief | null,
): string {
  switch (label.kind) {
    case 'resume': {
      const resume = t('episodes.resumeAt', { time: formatClock(label.positionSec) });
      const episode = now === null ? null : episodeText(t, now);
      return episode === null ? resume : `${episode} · ${resume}`;
    }
    case 'fromTop':
      return t('home.startFromTop');
    case 'shopping':
      // 홈쇼핑은 그 상품 처음부터다(2026-09-10 · 서버의 `resumePositionFor`) — 문구가 그것을 말한다
      return t('home.shopFromTop');
    case 'onair':
      return t('common.watchBroadcast');
    case 'pending':
      return t('gate.start');
  }
}

/**
 * 포커스가 들어온 카드를 레일 안에 드러낸다 (`docs/63` §6.3). **`smooth` 를 쓰지 않는다** —
 * 애니메이션 중 기하가 흔들려 다음 방향키가 엉뚱한 카드로 간다(§11-4).
 */
export function revealOnFocus(event: { currentTarget: Element }): void {
  event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
}
